/* eslint-disable no-unused-vars */
import { Box, Button, Grid, IconButton, Link, makeStyles, Menu, MenuItem, SvgIcon } from '@material-ui/core';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { Download as DownloadIcon, PlusCircle as PlusCircleIcon, Upload as UploadIcon } from 'react-feather';
import { Link as RouterLink } from 'react-router-dom';
import { Theme } from 'src/theme';
import { DownloadCSVData } from 'src/types/generic';
import Breadcrumbs from './Breadcrumbs';
import PageTitle from './PageTitle';
import MenuIcon from '@material-ui/icons/Menu';

interface ButtonInterface {
  onClick: () => void,
  icon?: ReactElement,
  label?: string,
  color?: 'default' | 'inherit' | 'primary' | 'secondary',
  variant?: any;
  disabled?: boolean;
}

//type dataColor: string = "default" | "inherit" | "primary" | "secondary";
interface HeaderProps {
  className?: string;
  breadcrumbs?: any;
  pageTitle?: string;
  pageLink?: string | null;
  enableImport?: boolean;
  enableExport?: boolean;
  mainHeader?: string;
  importClick?: () => void;
  exportClick?: () => void;
  addTitle?: string;
  showCancelButton?: boolean;
  cancelLink?: string;
  csvHeaders?: any;
  csvData?: any;
  handleCSVData?: (data: DownloadCSVData) => void;
  fileName?: string;
 
  extraButton?: ButtonInterface[];
  trailingActions?: ButtonInterface[];
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  buttonWidth: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));
const Header: FC<HeaderProps> = ({ className,
  breadcrumbs,
  pageTitle: title,
  pageLink,
  addTitle,
  enableImport,
  enableExport,
  showCancelButton,
  cancelLink,
  importClick,
  exportClick,
  mainHeader: header,
  csvHeaders,
  csvData = { 'data': [], 'download': false },
  handleCSVData,
  fileName,
  extraButton,
  trailingActions,
  ...rest }) => {
  const classes = useStyles();
  const csvLink = useRef(null);
  const { download, data } = csvData;
  const [postAddMenuAnchor, setPostAddMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (download) {
      setTimeout(() => {
        csvLink.current.link.click();
        handleCSVData({ data: [], download: false });
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [download]);

  return (
    <Grid
      className={clsx(classes.root, className)}
      container
      justifyContent="space-between"
      spacing={3}
      {...rest}
    >
      <Grid item sm={12} md={4}>
        <Breadcrumbs breadcrumbs={breadcrumbs} pageTitle={title} mainHeader={header} />
        <PageTitle title={title} />
        {/* show import/export buttons */}
        {enableImport || enableExport ?
          <Box mt={2}>
            {enableImport ? <Button onClick={importClick} startIcon={
              <SvgIcon fontSize="small">
                <UploadIcon />
              </SvgIcon>
            }>
              Import
            </Button> : ''}
            {enableExport ?
              <>
                <Button onClick={exportClick}
                  startIcon={
                    <SvgIcon fontSize="small">
                      <DownloadIcon />
                    </SvgIcon>
                  }>
                  Export
                </Button>
                <CSVLink headers={csvHeaders} data={data ? data : []} filename={fileName}
                  target="_blank"
                  style={{ textDecoration: 'none' }}
                  ref={csvLink}
                />
              </>
              : ''}
          </Box>
          : ''}

      </Grid>

      {/* page link and page title */}
      {pageLink || extraButton?.length || trailingActions?.length ?
        <Grid item sm={12} xs={12} md={8}>
          <Grid container spacing={3} justifyContent="flex-end" alignItems="center">
            {extraButton?.map((button, index) => {
              const { onClick, icon, color, variant, label, disabled } = button;
              return (
                <Grid key={`extra-${label || index}`} item className={classes.buttonWidth}>
                  <Button
                    className={classes.buttonWidth}
                    onClick={onClick}
                    startIcon={icon}
                    color={color}
                    variant={variant}
                    disabled={disabled}
                  >
                    {label}
                  </Button>
                </Grid>);
            })}
            {pageLink ? (
              <Grid item className={classes.buttonWidth}>
                <Link
                  variant="body1"
                  color="inherit"
                  to={`/app${pageLink}`}
                  component={RouterLink}
                  style={{ textDecoration: 'none' }}
                >
                  <Button
                    className={classes.buttonWidth}
                    color="secondary"
                    variant="contained"
                    startIcon={
                      <SvgIcon fontSize="small">
                        <PlusCircleIcon />
                      </SvgIcon>
                    }
                  >
                    {addTitle}
                  </Button>
                </Link>
              </Grid>
            ) : null}
            {trailingActions?.length ? (
              <Grid item>
                <IconButton
                  aria-label="More actions"
                  onClick={(event) => setPostAddMenuAnchor(event.currentTarget)}
                  color="default"
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={postAddMenuAnchor}
                  keepMounted
                  open={Boolean(postAddMenuAnchor)}
                  onClose={() => setPostAddMenuAnchor(null)}
                >
                  {trailingActions.map((button, index) => {
                    const { onClick, label, disabled } = button;
                    return (
                      <MenuItem
                        key={`post-add-${label || index}`}
                        onClick={() => {
                          setPostAddMenuAnchor(null);
                          onClick();
                        }}
                        disabled={disabled}
                      >
                        {label}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
        : ''}
      {/* handle cancel button        */}
      {
        showCancelButton && cancelLink ?
          <Grid item sm={12} xs={12} md={8}>
            <Grid container spacing={3} justifyContent="flex-end">
              <Grid item className={classes.buttonWidth}>
                <Button
                  className={classes.buttonWidth}
                  color="default"
                  variant="contained"
                  component={RouterLink}
                  to={cancelLink}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
          : ''
      }
    </Grid >
  );
};

Header.propTypes = {
  className: PropTypes.string
};

export default Header;
